import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, ArrowRight, FileText, Loader2, Regex, Search, SendHorizontal, Sparkles, Terminal } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { api, type KbAskRunRow, type KbGrepHit, type KbSearchResult } from '../lib/api';
import { RunPhaseHeader, RunTimelineView } from '../components/run/RunActivity';
import { RunCostChip, runUsageOf } from '../components/run/RunCost';
import { useAskStream, useLiveHeadline, useRunStream } from '../lib/run-stream';
import { ProjectTag, ScopeBar } from '../components/scope/ScopeBar';
import { ResizeHandle } from '../components/ui/resize-handle';
import { useResizablePanel } from '../lib/resizable';
import { useScope } from '../lib/scope';
import { cn } from '../lib/utils';
import { Markdown } from '../components/ui/markdown';

type Mode = 'ask' | 'grep';

const EXAMPLES = [
  'What did we decide about persistence, and why?',
  'Who owns the netcode work and what changed?',
  'What are the open risks on the milestone?',
];

/**
 * Asking the knowledge base, across whichever projects the scope covers.
 *
 * Org-level by default because a question rarely knows which project holds its answer — that is the
 * retrieval agent's job, and it already reads a scope rather than a project. The page used to fix the
 * project in its URL, which meant asking "what did we agree with the vendor" required first guessing
 * where the answer lived.
 */
export default function KbSearch() {
  const navigate = useNavigate();
  const scope = useScope();
  const projectId = scope.resolvedProjectId;
  const [searchParams, setSearchParams] = useSearchParams();

  const [mode, setMode] = useState<Mode>('ask');
  const [query, setQuery] = useState(() => searchParams.get('q') ?? '');
  const [regex, setRegex] = useState(false);
  const [result, setResult] = useState<KbSearchResult | null>(null);
  const [grepHits, setGrepHits] = useState<KbGrepHit[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * The live view of the ask in flight, opened on an id this page mints. The full URL is captured
   * at ask time rather than derived, so a scope change mid-ask cannot re-address a running stream;
   * a new ask is a new URL, which is also what resets the timeline.
   */
  const [askStream, setAskStream] = useState<string | null>(null);
  const ask = useAskStream(askStream);
  const askHeadline = useLiveHeadline(ask.timeline);
  /**
   * Past asks in this scope, newest first — every ask now leaves a row, so the page has a memory.
   *
   * Cursor-paginated and appended as the panel scrolls: the cursor is the last row's id, so a new
   * ask landing between two fetches shifts nothing. `historyEnd` is inferred from a short page —
   * asking the server for one more row than needed to learn it would double the row count lists
   * usually return for no reader-visible difference.
   */
  const [history, setHistory] = useState<KbAskRunRow[]>([]);
  const [historyEnd, setHistoryEnd] = useState(false);
  const historyBusy = useRef(false);
  /** A past ask opened from the history list; its timeline replays from the stored events. */
  const [viewing, setViewing] = useState<KbAskRunRow | null>(null);
  const replay = useRunStream(
    viewing ? projectId : null,
    viewing?.id ?? null,
    viewing?.status ?? null,
    'ask',
  );
  const replayHeadline = useLiveHeadline(replay.timeline);

  const HISTORY_PAGE = 20;

  const loadHistory = async (options: { reset?: boolean; cursor?: string } = {}) => {
    if (!projectId || historyBusy.current) return;
    historyBusy.current = true;
    try {
      const rows = await api.kb.askRuns(projectId, {
        scope: scope.mode,
        limit: HISTORY_PAGE,
        ...(options.cursor ? { cursor: options.cursor } : {}),
      });
      setHistory((current) => (options.reset || !options.cursor ? rows : [...current, ...rows]));
      setHistoryEnd(rows.length < HISTORY_PAGE);
    } catch {
      // History is a convenience; the page works without it.
    } finally {
      historyBusy.current = false;
    }
  };

  /** Fetch-more when the panel's own scroll nears its bottom — the panel scrolls, not the page. */
  const onHistoryScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (historyEnd || historyBusy.current || history.length === 0) return;
    const el = event.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 240) {
      void loadHistory({ cursor: history[history.length - 1]?.id });
    }
  };

  useEffect(() => {
    setHistory([]);
    setHistoryEnd(false);
    setViewing(null);
    void loadHistory({ reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, scope.mode]);

  /** A stored ask back into the shape the result panel renders — same answer, same citations. */
  const openHistory = (row: KbAskRunRow) => {
    setViewing(row);
    setAskStream(null);
    setGrepHits(null);
    setQuery(row.query);
    setResult({
      query: row.query,
      answer: row.answer ?? '',
      confidence: row.confidence,
      // Cast for the same reason every API response is: `KbCitation` is declared twice in api.ts
      // (provenance and search-answer shapes) and TS merges them into one interface demanding both.
      citations: (row.citations ?? []).map((c) => ({
        index: c.index,
        path: c.path,
        projectId: c.projectId,
        title: c.title,
        summary: c.summary ?? null,
      })) as KbSearchResult['citations'],
      visitedPaths: row.visitedPaths ?? [],
      fallbackUsed: row.fallbackUsed,
    });
  };

  const run = async (text = query) => {
    if (!projectId || text.trim().length < 2) return;
    setBusy(true);
    setError(null);
    setResult(null);
    setGrepHits(null);
    try {
      if (mode === 'ask') {
        // Stream first, then post: the order barely matters (the wire replays from the beginning
        // on connect) but this way the timeline is visibly waiting before the first event lands.
        const askId = crypto.randomUUID();
        setViewing(null);
        setAskStream(api.kb.askStreamUrl(projectId, askId));
        setResult(await api.kb.search(projectId, { query: text, scope: scope.mode, askId }));
        void loadHistory({ reset: true });
      } else {
        setAskStream(null);
        setGrepHits(await api.kb.grep(projectId, { pattern: text, regex, scope: scope.mode }));
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  /**
   * `?q=…` asks immediately — how the project overview hands a question over to this page.
   *
   * Waits for `projectId`, which arrives only once the project tree has loaded: firing before then
   * would be a no-op and the question would sit in the box unanswered. Guarded by a ref rather
   * than by clearing the parameter first, so a slow tree can't produce two agent runs.
   */
  const asked = useRef(false);
  useEffect(() => {
    const wanted = searchParams.get('q');
    if (!wanted || asked.current || !projectId) return;

    asked.current = true;
    void run(wanted);

    const next = new URLSearchParams(searchParams);
    next.delete('q');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, projectId, setSearchParams]);

  /**
   * Opens a result in the project that actually holds it.
   *
   * Paths are project-relative and repeat across projects, so a single "current project" would send
   * half the results of a cross-project answer to a page that does not exist. Falls back to the base
   * project only when a hit carries no project of its own.
   */
  const openNode = (path: string, hitProjectId?: string | null) => {
    const project = scope.projectFor(hitProjectId) ?? scope.project;
    if (project) navigate(`/p/${project.slug}/kb/${path}`);
  };

  // Same drag-to-resize as the knowledge base's pages panel, same bounds discipline: wide enough
  // to read a long question, never wide enough to crowd out the answer it restores.
  const historyPanel = useResizablePanel({
    initial: 320,
    min: 240,
    max: 480,
    side: 'left',
    storageKey: 'kb.ask.history.width',
  });

  return (
    <div className="flex h-screen overflow-hidden bg-surface">

      {/* Two columns, two scrollbars: the conversation and the record of past ones. The history
          panel owns its scroll because its pagination hangs off it — the main column growing a
          long answer must not be what triggers fetching older asks. */}
      <div className="pl-[var(--sidebar-w)] pr-[var(--rail-w)] flex-1 h-full flex overflow-hidden">
        {/* Every ask leaves a row — this is where they come back. Clicking one restores its
            answer and citations and replays its timeline from the stored events; scrolling the
            panel pages further into the past. */}
        <aside
          onScroll={onHistoryScroll}
          style={{ width: historyPanel.width }}
          className="flex-shrink-0 h-full overflow-y-auto border-r border-border bg-surface-subtle/40"
        >
          <div className="px-4 py-5">
            <p className="text-[10px] font-semibold text-ink-faint uppercase tracking-[0.07em] mb-3">
              Recent asks
            </p>

            {history.length === 0 ? (
              <p className="text-[11px] text-ink-faint">
                {historyEnd ? 'Nothing asked in this scope yet.' : 'Loading…'}
              </p>
            ) : (
              <div className="space-y-1.5">
                {history.map((row) => (
                  <button
                    key={row.id}
                    onClick={() => openHistory(row)}
                    className={cn(
                      'w-full px-3 py-2 rounded-md border text-left transition-colors bg-card',
                      viewing?.id === row.id
                        ? 'border-orange-400'
                        : 'border-border hover:border-orange-300',
                    )}
                  >
                    <span className="block text-xs text-ink-secondary line-clamp-2">{row.query}</span>
                    <span className="mt-1.5 flex items-center gap-2">
                      {row.status === 'FAILED' ? (
                        <Badge variant="danger">failed</Badge>
                      ) : row.status === 'PROCESSING' ? (
                        <Badge variant="warning">running</Badge>
                      ) : row.fallbackUsed ? (
                        <span
                          title="The agent loop didn't finish, so a plain search-and-summarize pass answered instead."
                          className="flex items-center gap-1 text-[10px] text-amber-600 dark:text-amber-400"
                        >
                          <AlertTriangle size={10} />fallback
                        </span>
                      ) : null}
                      {row.confidence !== null && row.status === 'COMPLETED' && (
                        <span className="text-[10px] text-ink-faint">{Math.round(row.confidence * 100)}%</span>
                      )}
                      <RunCostChip usage={runUsageOf(row)} />
                      <span className="flex-1" />
                      <span className="text-[10px] text-ink-faint">
                        {new Date(row.startedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                      </span>
                    </span>
                  </button>
                ))}

                {/* The tail says which of the three states the list is in — more below, fetching,
                    or done — because an infinite scroll that just stops is indistinguishable from
                    one that broke. */}
                <p className="pt-2 pb-1 text-center text-[10px] text-ink-faint">
                  {historyEnd ? 'End of history' : 'Scroll for older asks…'}
                </p>
              </div>
            )}
          </div>
        </aside>

        <ResizeHandle handleProps={historyPanel.handleProps} onDoubleClick={historyPanel.reset} />

        <div className="flex-1 h-full overflow-y-auto min-w-0">
        {/* Full width rather than a centered column: the history panel already spends 320px, and a
            timeline plus an answer read better with the room than with symmetric gutters. */}
        <div className="px-7 py-7">
          <div className="mb-4">
            <h1 className="text-lg font-bold text-ink">Ask the knowledge base</h1>
            <p className="text-xs text-ink-muted mt-1 max-w-2xl">
              An agent walks the tree — outline, hybrid search, grep, then reading the pages in full — and answers
              from what it actually read, citing the nodes it used. Reading{' '}
              {scope.isEverything
                ? 'every project'
                : `${scope.project?.name ?? 'this project'}${scope.mode === 'cone' ? ' and its sub-projects' : ''}`}
              .
            </p>
          </div>

          {/* One composer card: the text on top, and everything that shapes the question in a toolbar
              along its bottom edge — where to read (the scope), how (ask or grep), and the send. What
              used to be three stacked rows above a field is one rectangle, the way a chat composer
              puts its attachments and model pickers under the text rather than over it. The card
              carries the focus ring (`focus-within`) so the textarea can be borderless inside it. */}
          <div className="mb-6 border border-border rounded-lg bg-card focus-within:border-orange-400 transition-colors">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-3.5 text-ink-faint pointer-events-none" />
              <textarea
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); run(); }
                }}
                rows={mode === 'ask' ? 3 : 1}
                placeholder={mode === 'ask' ? 'What do you want to know?' : 'ERR_PERSISTENCE_TIMEOUT'}
                className={cn(
                  'w-full pl-9 pr-3 py-3 text-sm bg-transparent text-ink outline-none resize-none placeholder:text-ink-faint',
                  mode === 'grep' && 'font-mono text-xs',
                )}
                autoFocus
              />
            </div>

            {/* The toolbar. Scope first because it is the widest-reaching choice; the mode pills next;
                the hint and the send at the far right, hint first so the eye lands on the button. */}
            <div className="flex items-center gap-1.5 px-2 py-2 border-t border-border-subtle flex-wrap">
              <ScopeBar scope={scope} />

              <span className="w-px h-5 bg-border mx-1 hidden sm:block" aria-hidden />

              {([['ask', 'Ask', Sparkles], ['grep', 'Grep', Terminal]] as const).map(([value, label, Icon]) => (
                <button
                  key={value}
                  onClick={() => { setMode(value); setResult(null); setGrepHits(null); setAskStream(null); }}
                  className={cn(
                    'flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors border',
                    mode === value
                      ? 'bg-orange-500 text-white border-orange-500'
                      : 'bg-card text-ink-muted border-border hover:border-orange-300 hover:text-ink',
                  )}
                >
                  <Icon size={11} />{label}
                </button>
              ))}
              {mode === 'grep' && (
                <button
                  onClick={() => setRegex((r) => !r)}
                  title="Treat the pattern as a POSIX regular expression"
                  className={cn(
                    'flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors border',
                    regex
                      ? 'bg-ink text-surface border-ink'
                      : 'bg-card text-ink-muted border-border hover:text-ink',
                  )}
                >
                  <Regex size={11} />regex
                </button>
              )}

              <span className="ml-auto flex items-center gap-3">
                <span className="text-[10px] text-ink-faint hidden md:inline">
                  {mode === 'ask' ? 'Enter to ask · Shift+Enter for a new line' : 'Exact substring match'}
                </span>
                <button
                  type="button"
                  onClick={() => run()}
                  disabled={busy || query.trim().length < 2}
                  title={busy ? (mode === 'ask' ? 'Navigating…' : 'Searching…') : mode === 'ask' ? 'Ask' : 'Grep'}
                  aria-label={mode === 'ask' ? 'Ask' : 'Grep'}
                  className={cn(
                    'h-8 w-8 flex items-center justify-center rounded-md transition-colors',
                    'bg-orange-500 text-white hover:bg-orange-600 disabled:bg-surface-muted disabled:text-ink-faint disabled:cursor-not-allowed',
                  )}
                >
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <SendHorizontal size={14} />}
                </button>
              </span>
            </div>
          </div>

          {error && (
            <p className="mb-4 text-xs text-danger-text bg-danger-bg border border-danger-ring rounded px-3 py-2">{error}</p>
          )}

          {!result && !grepHits && !busy && (
            <div className="space-y-1.5">
              <p className="text-[10px] font-semibold text-ink-faint uppercase tracking-[0.07em]">Try</p>
              {EXAMPLES.map((example) => (
                <button
                  key={example}
                  onClick={() => { setMode('ask'); setQuery(example); run(example); }}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-md border border-border bg-card text-left hover:border-orange-300 transition-colors group"
                >
                  <Search size={11} className="text-ink-faint flex-shrink-0" />
                  <span className="text-xs text-ink-secondary flex-1">{example}</span>
                  <ArrowRight size={11} className="text-ink-faint opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                </button>
              ))}
            </div>
          )}

          {/* Ask result */}
          {result && (
            <div className="space-y-4">
              <div className="border border-border rounded-lg bg-card overflow-hidden">
                <div className="px-4 py-2.5 border-b border-border-subtle bg-surface-subtle flex items-center justify-between gap-2 flex-wrap">
                  <span className="text-[10px] font-semibold text-ink-faint uppercase tracking-[0.07em]">Answer</span>
                  <div className="flex items-center gap-2">
                    {result.confidence !== null && (
                      <Badge variant={result.confidence >= 0.7 ? 'success' : result.confidence >= 0.4 ? 'warning' : 'danger'}>
                        {Math.round(result.confidence * 100)}% covered
                      </Badge>
                    )}
                    {result.fallbackUsed && (
                      <span
                        title="The agent loop didn't finish, so a plain search-and-summarize pass answered instead."
                        className="flex items-center gap-1 text-[10px] text-amber-600 dark:text-amber-400"
                      >
                        <AlertTriangle size={10} />fallback
                      </span>
                    )}
                  </div>
                </div>
                <div className="px-4 py-4">
                  <Markdown>{result.answer || '_No answer returned._'}</Markdown>
                </div>
              </div>

              {result.citations.length > 0 && (
                <div>
                  <p className="text-[10px] font-semibold text-ink-faint uppercase tracking-[0.07em] mb-2">
                    Sources ({result.citations.length})
                  </p>
                  <div className="border border-border rounded-lg overflow-hidden divide-y divide-border-subtle bg-card">
                    {result.citations.map((citation) => (
                      <button
                        key={`${citation.projectId ?? ''}:${citation.path}`}
                        onClick={() => openNode(citation.path, citation.projectId)}
                        className="w-full flex items-start gap-2.5 px-3 py-2.5 text-left hover:bg-surface-subtle transition-colors"
                      >
                        <span className="text-[10px] font-mono font-semibold text-orange-500 mt-0.5 flex-shrink-0">
                          [{citation.index}]
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-1.5">
                            <span className="text-xs font-medium text-ink truncate">{citation.title}</span>
                            {/* Which project the answer drew this from — the part a cross-project
                                answer is otherwise silent about. */}
                            <ProjectTag name={scope.projectFor(citation.projectId)?.name} />
                          </span>
                          <span className="block text-[10px] font-mono text-ink-faint truncate">{citation.path}</span>
                          {citation.summary && (
                            <span className="block text-[11px] text-ink-muted mt-0.5">{citation.summary}</span>
                          )}
                        </span>
                        <ArrowRight size={11} className="text-ink-faint flex-shrink-0 mt-0.5" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* The navigation trail — how the agent got to its answer, and what it looked
                  at but chose not to cite. */}
              {result.visitedPaths.length > 0 && (
                <details className="border border-border rounded-lg bg-card">
                  <summary className="px-3 py-2 text-[11px] text-ink-muted cursor-pointer hover:text-ink">
                    Nodes opened during the run ({result.visitedPaths.length})
                  </summary>
                  {/* Text, not links: across several projects the agent labels these `Project:path`,
                      which is exactly what makes the trail readable and exactly what makes it not an
                      address. The cited sources above are the navigable list. */}
                  <div className="px-3 pb-3 flex flex-wrap gap-1.5">
                    {result.visitedPaths.map((path) => (
                      <span
                        key={path}
                        className={cn(
                          'px-1.5 py-0.5 rounded text-[10px] font-mono',
                          result.citations.some((c) => path.endsWith(c.path))
                            ? 'bg-orange-100 dark:bg-orange-950/50 text-orange-700 dark:text-orange-400'
                            : 'bg-surface-muted text-ink-faint',
                        )}
                      >
                        {path}
                      </span>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}

          {/* How the ask is going — and, once answered, how it got there. The same run console
              every other agent renders; loading is always false on the live path because there is
              no stored half to wait for, only the wire. A past ask replays its stored timeline
              through the standard events-then-stream hook instead. */}
          {mode === 'ask' && askStream && !viewing && (
            <div className={cn('space-y-3', result && 'mt-6')}>
              <RunPhaseHeader timeline={ask.timeline} headline={askHeadline} live={ask.live} />
              <RunTimelineView timeline={ask.timeline} live={ask.live} loading={false} />
              {ask.error && <p className="text-[11px] text-ink-faint">{ask.error}</p>}
            </div>
          )}
          {mode === 'ask' && viewing && (
            <div className="mt-6 space-y-3">
              <div className="flex items-center justify-between">
                <RunPhaseHeader timeline={replay.timeline} headline={replayHeadline} live={replay.live} />
                <RunCostChip usage={runUsageOf(viewing)} />
              </div>
              <RunTimelineView timeline={replay.timeline} live={replay.live} loading={replay.loading} />
            </div>
          )}

          {/* Grep result */}
          {grepHits && (
            grepHits.length === 0 ? (
              <div className="border border-dashed border-border rounded-lg py-12 text-center">
                <Terminal size={20} className="text-border mx-auto mb-3" />
                <p className="text-sm text-ink-secondary mb-1">No literal matches</p>
                <p className="text-xs text-ink-faint">Try Ask instead — it matches meaning, not characters.</p>
              </div>
            ) : (
              <div>
                <p className="text-[10px] font-semibold text-ink-faint uppercase tracking-[0.07em] mb-2">
                  {grepHits.length} match{grepHits.length === 1 ? '' : 'es'}
                </p>
                <div className="border border-border rounded-lg overflow-hidden divide-y divide-border-subtle bg-card">
                  {grepHits.map((hit) => (
                    <button
                      key={hit.id}
                      onClick={() => openNode(hit.path, hit.projectId)}
                      className="w-full flex items-start gap-2.5 px-3 py-2.5 text-left hover:bg-surface-subtle transition-colors"
                    >
                      <FileText size={12} className="text-ink-faint mt-0.5 flex-shrink-0" />
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-1.5">
                          <span className="text-xs font-medium text-ink truncate">{hit.title}</span>
                          <ProjectTag name={scope.projectFor(hit.projectId)?.name} />
                        </span>
                        <span className="block text-[10px] font-mono text-ink-faint truncate">{hit.path}</span>
                        {hit.matchLine && (
                          <span className="block text-[11px] text-ink-secondary font-mono mt-1 truncate">{hit.matchLine}</span>
                        )}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )
          )}
        </div>
        </div>

      </div>
    </div>
  );
}
